import { computed, onMounted, ref, watch } from "vue";
import { useStorage } from "@vueuse/core";
import { useRoute, useRouter } from "vue-router";
import { Encoded, generateKeyPair } from "@aeternity/aepp-sdk";

import { LS_KEYS } from "@/utils/constants";
import { Decimal } from "@/libs/decimal";
import { useAccounts } from "@/stores";
import { useCommunityFactory } from "./useCommunityFactory";

interface InvitationInfo {
  inviter: Encoded.AccountAddress;
  secretKey: string;
  date: number;
  amount: number;
}

const INVITE_CODE_QUERY_KEY = "invite_code";

let initialized = false;

/** privateKey */
const invitationCode = ref<string>();
const invitationList = useStorage<InvitationInfo[]>(LS_KEYS.inviteList, []);

export function useInvitation() {
  const router = useRouter();
  const route = useRoute();
  const { getAffiliationTreasury } = useCommunityFactory();
  const accounts = useAccounts();

  const activeAccountInviteList = computed(() =>
    invitationList.value.filter(
      ({ inviter }) => inviter === accounts.activeAccount
    )
  );

  async function generateInviteKey(amount?: number): Promise<string> {
    if (!amount) {
      throw new Error("Please enter an amount");
    }

    const affiliationTreasury = await getAffiliationTreasury();
    const amountValue = BigInt(Decimal.from(amount).bigNumber);
    const { publicKey, secretKey } = generateKeyPair();

    const redemptionFeeCover = 10n ** 15n;
    // TODO Bulk invites
    await affiliationTreasury.registerInvitationCode(
      [publicKey],
      redemptionFeeCover,
      amountValue
    );

    invitationList.value.push({
      inviter: accounts.activeAccount!,
      secretKey,
      amount,
      date: Date.now(),
    });

    return secretKey;
  }

  /**
   * @returns https://domain.com/#invite_code=[SECRET_KEY]
   */
  function prepareInviteLink(secretKey: string): string {
    return `${location.protocol}//${location.host}#${INVITE_CODE_QUERY_KEY}=${secretKey}`;
  }

  function removeStoredInvite(secretKey: string) {
    invitationList.value = invitationList.value.filter(
      (invitation) => invitation.secretKey !== secretKey
    );
  }

  function resetInviteCode() {
    localStorage.removeItem(LS_KEYS.inviteCode);
    router.push({ hash: "" });
    invitationCode.value = undefined;
  }

  onMounted(() => {
    const code = localStorage.getItem(LS_KEYS.inviteCode);
    if (code) {
      invitationCode.value = code;
    }
  });

  if (!initialized) {
    initialized = true;

    try {
      const res = localStorage.getItem(LS_KEYS.inviteList);
      if (res) {
        invitationList.value = JSON.parse(res) || [];
      }
    } catch (error) {
      /* NOOP */
    }

    watch(
      () => route.hash,
      (hash) => {
        if (hash) {
          const hashParsed = new URLSearchParams(hash.replace("#", ""));
          const inviteCode = hashParsed.get(INVITE_CODE_QUERY_KEY);
          if (inviteCode) {
            invitationCode.value = inviteCode;
            localStorage.setItem(LS_KEYS.inviteCode, invitationCode.value);
          }
        }
      },
      { immediate: true, deep: true }
    );
  }

  return {
    invitationCode,
    invitationList,
    activeAccountInviteList,
    generateInviteKey,
    resetInviteCode,
    removeStoredInvite,
    prepareInviteLink,
  };
}
